import ApiResponseV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/ApiResponseV2";
import { IPage } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/IPage";
import HttpClientV2 from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/HttpClientV2";
import { Funder, FunderList, FunderListCollaborator, FunderNote, NextContact } from "@/types/FunderTypes";
import { GenerateSmartlistNameDTO } from "@/types/GenerateSmartlistNameDTO";
import { GeneratedSmartListNames } from "@/types/GeneratedSmartListNames";
import { download } from "@/utils/DownloadManyFilesZip";
import { convertBase64ToFile } from "./LazoFileService";

class FunderListService {
  path: string;

  constructor(path: string) {
    this.path = path;
  }

  createList(funderList: FunderList): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.post(`${this.path}/`, funderList);
  }

  createListWithFunders(funders: Funder[], name: string): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.post(
      `${this.path}/create-with-funder`,
      {
        funders,
        name,
      } as FunderList,
      {
        axiosConfig: { timeout: 1800000 },
      }
    );
  }

  getFunderListsPaginated(page: number, pageSize: number): Promise<ApiResponseV2<IPage<FunderList>>> {
    return HttpClientV2.get(`${this.path}/all?page=${page}&pageSize=${pageSize}`);
  }

  getAllWithFullAccess(): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.get(`${this.path}/all/full-access`);
  }

  getByExternalId(externalId: string): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.get(`${this.path}/${externalId}`);
  }

  addFunders(externalId: string, funders: Funder[]): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.post(`${this.path}/${externalId}/funder`, funders);
  }

  addFundersToMultipleLists(funderListExternalIds: string[], funders: Funder[]): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.post(`${this.path}/funder`, {
      funderListExternalIds,
      funders,
    });
  }

  delete(externalId: string): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.delete(`${this.path}/${externalId}`);
  }

  update(externalId: string, funderList: FunderList): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.put(`${this.path}/${externalId}`, funderList);
  }

  addCollaborators(externalId: string, collaborators: FunderListCollaborator[]): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.post(`${this.path}/${externalId}/collaborator`, collaborators);
  }

  removeFundersFromList(externalId: string, funders: Funder[]): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.put(`${this.path}/${externalId}/funder/disable`, funders);
  }

  updateFunderNotes(funderListExternalId: string, funderExternalId: string, notes: FunderNote[]): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.put(`${this.path}/${funderListExternalId}/funder/${funderExternalId}/note`, notes);
  }

  deleteFunderNote(funderListExternalId: string, noteExternalId: string): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.delete(`${this.path}/${funderListExternalId}/funder/note/${noteExternalId}`);
  }

  updateNextContacts(funderListExternalId: string, funderExternalId: string, nextContacts: NextContact[]): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.put(`${this.path}/${funderListExternalId}/funder/${funderExternalId}/next-contact`, nextContacts);
  }

  deleteNextContacts(funderListExternalId: string, nextContactExternalId: string): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.delete(`${this.path}/${funderListExternalId}/funder/next-contact/${nextContactExternalId}`);
  }

  getRecommendedLists(): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.get(`${this.path}/recommended`);
  }

  updateRecommendedList(externalId: string, funderList: FunderList): Promise<ApiResponseV2<FunderList>> {
    return HttpClientV2.put(`${this.path}/${externalId}/recommended`, funderList);
  }

  getFundersPaginated(
    funderListExternalId: string,
    page: number,
    pageSize: number,
    orderBy?: string,
    sortDirection?: string
  ): Promise<ApiResponseV2<IPage<Funder>>> {
    return HttpClientV2.get(
      `${this.path}/${funderListExternalId}/funders?page=${page}&pageSize=${pageSize}&orderBy=${orderBy}&sortDirection=${sortDirection}`
    );
  }

  updateFunder(funderListExternalId: string, funder: Funder): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.put(`${this.path}/${funderListExternalId}/funder`, funder);
  }

  search(search: string): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.get(`${this.path}/search?name=${search}`);
  }

  createCustomFunder(funder: Funder, funderListExternalId: string): Promise<ApiResponseV2<Funder>> {
    return HttpClientV2.post(`${this.path}/${funderListExternalId}/create-custom`, funder);
  }

  generateSmartListNames(dto: GenerateSmartlistNameDTO): Promise<ApiResponseV2<{ content: GeneratedSmartListNames }>> {
    return HttpClientV2.post(`${this.path}/generate-recommended-smartlist-names`, dto);
  }

  getByUserAndFunderUUID(funderUUID: string): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.get(`${this.path}/funder/${funderUUID}/funder-lists`);
  }

  deleteFunder(funderUUID: string, funderListExternalId: string): Promise<ApiResponseV2<FunderList[]>> {
    return HttpClientV2.delete(`${this.path}/${funderListExternalId}/funder/${funderUUID}`);
  }

  exportFunderListToXlsx(funderListExternalId: string, fileName: string): Promise<void> {
    return new Promise((res, rej) => {
      HttpClientV2.get(`${this.path}/${funderListExternalId}/export/xlsx`, {
        axiosConfig: {
          timeout: 60000,
        },
      }).then((r) => {
        r.onSuccess((c: string) => {
          download(convertBase64ToFile(c, "file"), fileName + ".xlsx");
          res();
        });
        r.onError((e) => {
          rej(e);
        });
      });
    });
  }
}

export default new FunderListService("/api/funder-list");
